<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { Bell } from 'lucide-vue-next'
import AppSidebar from '@/components/AppSidebar.vue'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'

const route = useRoute()
const pageTitle = computed(() => (route.meta.title as string | undefined) || 'Dashboard')

onMounted(async () => {
  // Silent refresh for users who already opted in; never prompts (see My Account).
  const { usePushNotifications } = await import('@/composables/usePushNotifications')
  void usePushNotifications().syncToken()
})
</script>

<template>
  <SidebarProvider>
    <AppSidebar />
    <SidebarInset class="h-svh min-h-0 overflow-hidden">
      <header class="flex h-14 shrink-0 items-center gap-2 border-b px-4">
        <SidebarTrigger class="-ml-1" />
        <Separator
          orientation="vertical"
          class="mr-2 data-[orientation=vertical]:h-4"
        />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem class="hidden md:block">
              <BreadcrumbPage>Home</BreadcrumbPage>
            </BreadcrumbItem>
            <BreadcrumbSeparator class="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>{{ pageTitle }}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <Button variant="ghost" size="icon-sm" as-child class="ml-auto" title="Notifications">
          <RouterLink to="/notifications">
            <Bell class="size-4" />
          </RouterLink>
        </Button>
      </header>

      <main class="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
        <RouterView class="min-w-0" />
      </main>
    </SidebarInset>
  </SidebarProvider>
</template>
